import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Paper, Typography } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import KanbanCard from "./KanbanCard";
import logger from "../../utils/logger";
import { i18n } from "../../translate/i18n";

// 0259 UX: Maqueta compacta + responsive (zoom, pantallas pequeñas)
const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "nowrap",
    height: "100%",
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.5),
    overflowX: "auto",
    overflowY: "hidden",
    // Adaptar al zoom: minWidth en columnas fuerza scroll horizontal
    "-webkit-overflow-scrolling": "touch",
  },
  column: {
    flex: "0 0 auto",
    width: 280,
    minWidth: 240,
    maxWidth: 320,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  columnHeader: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  columnTitle: {
    fontWeight: 600,
    fontSize: "0.85rem",
    lineHeight: 1.3,
  },
  columnCount: {
    color: "white",
    borderRadius: "50%",
    width: 20,
    height: 20,
    minWidth: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.7rem",
    fontWeight: "bold",
  },
  cardsContainer: {
    padding: theme.spacing(0.75),
    maxHeight: "calc(100vh - 180px)",
    minHeight: 80,
    overflowY: "auto",
    overflowX: "hidden",
  },
}));

const KanbanBoard = ({ tickets, tags, onCardMove, onCardClick, onColumnsReorder }) => {
  const classes = useStyles();
  const history = useHistory();
  const [localTickets, setLocalTickets] = useState(tickets);
  const [isMoving, setIsMoving] = useState(false);

  // ✅ Actualizar tickets locales cuando cambien los props
  React.useEffect(() => {
    setLocalTickets(tickets);
  }, [tickets]);

  // ✅ Filtrar solo etiquetas que tienen kanban activado (0258 C1: columnas por perfil)
  // eslint-disable-next-line eqeqeq
  const etiquetasKanban = tags.filter((tag) => tag.kanban == 1 || tag.kanban === true);

  // ✅ 0258 C2: Posicionar por kanbanProfileStage cuando hay cita, sino por tags
  // Comparación case-insensitive: "SESIÓN CONFIRMADA" coincide con "Sesión Confirmada" (DB)
  // "SESIÓN COMPLETA" y "SESIÓN COMPLETADA" se consideran equivalentes
  const matchKanbanStage = (tagName, stage) => {
    if (!tagName || !stage) return false;
    const t = String(tagName).trim().toLowerCase();
    const st = String(stage).trim().toLowerCase();
    if (t === st) return true;
    return t.includes("completa") && st.includes("completa");
  };

  /**
   * 0391 F2 — Carriles alternativos cuando el perfil no tiene una columna concreta.
   *
   * No todos los perfiles definen los mismos carriles: 'Educación' no tiene
   * "Pendiente Confirmar" y 'Reservas' sí. Sin esta cadena, un ticket cuya clave no
   * existiera en el tablero no caería en ninguna columna y desaparecería de la vista
   * —exactamente el fallo que F2 corrige, reaparecido por otro camino.
   */
  const STAGE_FALLBACK = {
    unassigned: "interested",
    pending_confirm: "interested",
    in_progress: "confirmed",
  };

  /** Resuelve un carril siguiendo la clave y su cadena de alternativas. */
  const resolveByStageKey = (stageKey, columns) => {
    let key = stageKey;
    const visited = new Set();
    while (key && !visited.has(key)) {
      visited.add(key);
      const byKey = columns.find((c) => c.stageKey && c.stageKey === key);
      if (byKey) return byKey;
      key = STAGE_FALLBACK[key];
    }
    return null;
  };

  /**
   * Empareja por CLAVE, no por el nombre visible.
   *
   * El backend calculaba 'SESIÓN CONFIRMADA' y el carril se llama 'Sesion Confirmada':
   * como la comparación por nombre no normaliza acentos, no casaban y la tarjeta se caía
   * del tablero.
   *
   * ORDEN DE PRIORIDAD — importa, y aquí se corrigió una regresión:
   *
   *  1. Si el ticket TIENE cita, la cita gobierna el carril. Ese es el modelo.
   *  2. Si NO tiene cita, manda la etiqueta que un humano le puso: es una decisión
   *     explícita y debe pesar más que cualquier valor por defecto.
   *  3. Sin cita ni etiqueta, la posición por defecto del embudo (`unassigned` para
   *     tickets sin tomar, `interested` para los ya tomados).
   *
   * Sin el paso 2, etiquetar manualmente un ticket sin cita no movía la tarjeta: el
   * `stageKey` calculado termina en `ELSE 'interested'`, así que NUNCA es nulo y
   * resolvía siempre a "Interesado" antes de llegar a mirar las etiquetas.
   */
  const findColumnForTicket = (ticket, columns) => {
    const hasAppointment = Boolean(ticket.currentAppointmentId);

    if (hasAppointment) {
      const byKey = resolveByStageKey(ticket.stageKey, columns);
      if (byKey) return byKey;
      if (ticket.kanbanProfileStage && typeof ticket.kanbanProfileStage === "string") {
        const byName = columns.find((c) => matchKanbanStage(c.name, ticket.kanbanProfileStage));
        if (byName) return byName;
      }
      return null;
    }

    if (Array.isArray(ticket.tags)) {
      const tagged = columns.find((c) => ticket.tags.some((tt) => tt && tt.id === c.id));
      if (tagged) return tagged;
    }

    return resolveByStageKey(ticket.stageKey, columns);
  };

  const ticketPerteneceAColumna = (ticket, etiquetasKanbanList) => {
    if (findColumnForTicket(ticket, etiquetasKanbanList)) return true;
    if (ticket.tags && Array.isArray(ticket.tags)) {
      // eslint-disable-next-line eqeqeq
      return ticket.tags.some((tag) => tag && (tag.kanban == 1 || tag.kanban === true));
    }
    return false;
  };

  const ticketsSinEtiquetas = localTickets.filter((ticket) => !ticketPerteneceAColumna(ticket, etiquetasKanban));
  
  // ✅ Etiquetas kanban procesadas
  
  // ✅ DEBUG DETALLADO: Verificar estructura de tickets antes del filtrado
  React.useEffect(() => {
    // ✅ CORREGIDO: Solo ejecutar una vez cuando se cargan los tickets inicialmente
    if (localTickets.length > 0 && localTickets !== tickets) {
      logger.dashboard.debug('🔍 [DEBUG] Estructura de tickets recibidos:', {
        totalTickets: localTickets.length,
        sampleTickets: localTickets.slice(0, 3).map(t => ({
          id: t.id,
          hasTags: !!t.tags,
          tagsType: typeof t.tags,
          tagsIsArray: Array.isArray(t.tags),
          tagsLength: t.tags?.length || 0,
          hasCompraRealizada: t.tags?.some(tag => tag.id === 4) || false
        }))
      });
    }
  }, [localTickets, tickets]);

  // ✅ 0258 C2: Asignar cada ticket a una sola columna. Prioridad: kanbanProfileStage > tags.
  const ticketsPorEtiqueta = {};
  etiquetasKanban.forEach((tag) => {
    ticketsPorEtiqueta[tag.id] = [];
  });
  localTickets.forEach((ticket) => {
    // Prioridad 1: la clave de carril (con respaldo por nombre para tableros libres)
    const colTag = findColumnForTicket(ticket, etiquetasKanban);
    if (colTag && ticketsPorEtiqueta[colTag.id]) {
      ticketsPorEtiqueta[colTag.id].push(ticket);
      return;
    }
    // Prioridad 2: tags con kanban=1 (primer tag que coincida)
    if (ticket.tags && Array.isArray(ticket.tags)) {
      for (const t of etiquetasKanban) {
        const match = ticket.tags.some((tt) => tt && tt.id === t.id);
        if (match) {
          ticketsPorEtiqueta[t.id].push(ticket);
          return;
        }
      }
    }
    // Si no coincide con ninguna columna, va en ticketsSinEtiquetas (ABIERTOS)
  });


  // ✅ Debug para verificar etiquetas kanban y tickets
  React.useEffect(() => {
    // ✅ CORREGIDO: Solo ejecutar cuando cambien los datos reales, no en cada render
    if (localTickets.length > 0 && localTickets !== tickets) {
      logger.dashboard.debug('🔄 Kanban dinámico actualizado:', {
        totalTickets: localTickets.length,
        sinEtiquetas: ticketsSinEtiquetas.length,
        etiquetasKanban: etiquetasKanban.map(tag => ({
          id: tag.id,
          name: tag.name,
          kanban: tag.kanban,
          tickets: ticketsPorEtiqueta[tag.id]?.length || 0
        }))
      });
      
      // ✅ DEBUG EXTENDIDO: Verificar tickets sin etiquetas
      if (ticketsSinEtiquetas.length > 0) {
        logger.dashboard.debug('🔄 [KanbanBoard] Tickets ABIERTOS encontrados:', 
          ticketsSinEtiquetas.slice(0, 5).map(t => ({
            id: t.id,
            contactName: t.contact?.name,
            tags: t.tags?.length || 0,
            tagNames: t.tags?.map(tag => tag.name) || []
          }))
        );
      } else {
        logger.dashboard.warn('⚠️ [KanbanBoard] NO HAY TICKETS SIN ETIQUETAS - ABIERTOS estará vacío');
        
        // ✅ DEBUG: Verificar estructura de tickets recibidos
        if (localTickets.length > 0) {
          logger.dashboard.debug('🔄 [KanbanBoard] Estructura de tickets recibidos:', 
            localTickets.slice(0, 3).map(t => ({
              id: t.id,
              hasTags: !!t.tags,
              tagsLength: t.tags?.length || 0,
              tagsType: typeof t.tags,
              tagsIsArray: Array.isArray(t.tags),
              rawTags: t.tags
            }))
          );
        }
      }
    }
  }, [localTickets, tickets, etiquetasKanban, ticketsPorEtiqueta, ticketsSinEtiquetas]); // ✅ CORREGIDO: Dependencias completas

  // ✅ 0259 P5: Paleta maqueta (fallback cuando tag.color vacío)
  const PALETA_MAQUETA = ["#2196F3", "#FF9800", "#4CAF50", "#9E9E9E", "#F44336"]; // azul, naranja, verde, gris, rojo
  const getColorColumna = (tag, index) => {
    if (tag?.color && String(tag.color).trim()) return tag.color;
    return PALETA_MAQUETA[index % PALETA_MAQUETA.length];
  };

  // ✅ 0261: Solo columnas por perfil. Sin ABIERTOS (KanbanBoard solo se muestra cuando agendaActive).
  const columns = etiquetasKanban.map((tag, index) => ({
    id: `tag-${tag.id}`,
    // 0391: `tagId` y `stageKey` viajan con la columna para poder reordenar carriles y
    // resolver el destino de una tarjeta sin volver a buscar la etiqueta.
    tagId: tag.id,
    stageKey: tag.stageKey ?? null,
    title: tag.name.toUpperCase(),
    tickets: ticketsPorEtiqueta[tag.id] || [],
    color: getColorColumna(tag, index)
  }));



  const handleCardClick = (ticket) => {
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleCalendarClick = (e) => {
    e?.stopPropagation?.();
    history.push({ pathname: "/appointments", state: { tabIndex: 1 } });
  };

  const handleScheduleModalClose = () => {
    setScheduleModalOpen(false);
    setSelectedTicket(null);
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId, type } = result;

    /**
     * Reordenar carriles (0391 · corrección B).
     *
     * Se delega en el mismo endpoint que usa la pantalla de Etiquetas, así que mover aquí
     * o allí produce exactamente el mismo resultado: ambos escriben `displayOrder`.
     */
    if (type === "COLUMN") {
      if (destination.index === source.index) return;
      const ordered = Array.from(columns);
      const [moved] = ordered.splice(source.index, 1);
      ordered.splice(destination.index, 0, moved);
      if (onColumnsReorder) await onColumnsReorder(ordered.map((c) => c.tagId));
      return;
    }

    if (source.droppableId === destination.droppableId) {
      // Mismo column, solo reordenar
      return;
    }

    // Diferente column, mover ticket o cita (0266: cardId puede ser "appointment-X" o "ticket-X")
    try {
      setIsMoving(true);
      const sourceLaneId = source.droppableId;
      const targetLaneId = destination.droppableId;

      // ✅ ACTUALIZACIÓN INMEDIATA LOCAL — buscar por cardId o id
      const ticketToMove = localTickets.find(
        t => String(t.cardId || t.id) === String(draggableId)
      );
      if (ticketToMove) {
        const isAppointmentCard = String(draggableId).startsWith('appointment-');
        const updatedTicket = { ...ticketToMove };

        if (targetLaneId.startsWith('tag-')) {
          const targetTagId = parseInt(targetLaneId.replace('tag-', ''), 10);
          const targetTag = etiquetasKanban.find(tag => tag.id === targetTagId);

          if (isAppointmentCard && targetTag) {
            // 0266: Card de cita — actualizar kanbanProfileStage (la columna viene del status de la cita)
            updatedTicket.kanbanProfileStage = targetTag.name;
            // 0391 F2: la clave manda sobre el nombre al recolocar la tarjeta; sin
            // actualizarla, el siguiente render la devolvería a su columna anterior.
            updatedTicket.stageKey = targetTag.stageKey ?? updatedTicket.stageKey;
            logger.dashboard.debug('🔄 Card cita: stageKey →', targetTag.stageKey, '| nombre →', targetTag.name);
          } else if (targetTag) {
            // Card de ticket — actualizar tags
            if (sourceLaneId.startsWith('tag-')) {
              const sourceTag = etiquetasKanban.find(tag => tag.id === parseInt(sourceLaneId.replace('tag-', ''), 10));
              if (sourceTag) {
                updatedTicket.tags = (updatedTicket.tags || []).filter(tag => tag.id !== sourceTag.id);
              }
            }
            const alreadyHasTag = (updatedTicket.tags || []).some(tag => tag.id === targetTag.id);
            if (!alreadyHasTag) {
              updatedTicket.tags = [...(updatedTicket.tags || []), targetTag];
            }
          }
        }

        setLocalTickets(prev =>
          prev.map(t =>
            String(t.cardId || t.id) === String(draggableId) ? updatedTicket : t
          )
        );
      }

      // Llamar a la función de movimiento (0266: cardId puede ser appointment-X o ticket-X)
      await onCardMove(draggableId, sourceLaneId, targetLaneId);
      
      // ✅ Confirmación visual de movimiento exitoso
      logger.dashboard.debug('✅ Movimiento completado exitosamente');
      
    } catch (error) {
      logger.dashboard.error("Error moving card:", error);
      // Si hay error, revertir el cambio local
      setLocalTickets(tickets);
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <>
      {/**
       * Un solo contexto de arrastre con DOS tipos (0391 · corrección B):
       *  - `COLUMN`: reordena carriles. El asa vive en la cabecera, no en toda la columna,
       *    para que nadie mueva un carril entero al intentar coger una tarjeta.
       *  - `CARD`: mueve tarjetas, como hasta ahora.
       * El orden que se define aquí es el mismo `displayOrder` que edita la pantalla de
       * Etiquetas: mover en un sitio se refleja en el otro.
       */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="kanban-board" direction="horizontal" type="COLUMN">
          {(boardProvided) => (
        <div
          className={classes.root}
          ref={boardProvided.innerRef}
          {...boardProvided.droppableProps}
        >
          {columns.map((column, columnIndex) => (
            <Draggable
              key={`col-${column.id}`}
              draggableId={`col-${column.id}`}
              index={columnIndex}
              isDragDisabled={!column.tagId}
            >
              {(colProvided, colSnapshot) => (
            <Paper
              className={classes.column}
              elevation={colSnapshot.isDragging ? 6 : 1}
              ref={colProvided.innerRef}
              {...colProvided.draggableProps}
            >
              <div
                className={classes.columnHeader}
                style={{ borderLeft: `4px solid ${column.color}` }}
                {...colProvided.dragHandleProps}
                title={i18n.t("kanban.dragColumnHint")}
              >
                <Typography className={classes.columnTitle} style={{ color: column.color }}>
                  {column.title}
                </Typography>
                <div
                  className={classes.columnCount}
                  style={{ backgroundColor: column.color }}
                >
                  {column.tickets.length}
                </div>
              </div>

              <Droppable droppableId={column.id} type="CARD">
                {(provided, snapshot) => (
                  <div 
                    className={classes.cardsContainer}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {column.tickets.map((ticket, index) => (
                      <Draggable
                        key={(ticket.cardId || ticket.id).toString()}
                        draggableId={(ticket.cardId || ticket.id).toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <KanbanCard
                              ticket={ticket}
                              isMoving={isMoving}
                              isDragging={snapshot.isDragging}
                              onCardClick={handleCardClick}
                              onCalendarClick={handleCalendarClick}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </Paper>
              )}
            </Draggable>
          ))}
          {boardProvided.placeholder}
        </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default KanbanBoard; 